import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Container from '@/components/Container';
import Button from '@/components/Button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import FieldError from '@/components/FieldError';
import { verifyMfaReset } from '@/services/auth/auth.service';
import { useMfaTexts } from '../../mfaTexts';

/**
 * Self-service MFA recovery — step 2. The email arrives via router state from the
 * request screen; on refresh (state lost) we bounce back to restart the flow. On
 * a valid code the backend clears MFA and returns a fresh `mfa-setup` token, so
 * we route straight into re-enrolment (`/login/mfa-setup`) — no session is minted
 * until the user finishes setting up their new authenticator.
 */
export default function MfaResetCode() {
  const location = useLocation();
  const email = (location.state as { email?: string } | null)?.email;
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const t = useMfaTexts();
  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    codeInputRef.current?.focus();
  }, []);

  if (!email) return <Navigate to="/login/mfa/reset" replace />;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { data, error: err } = await verifyMfaReset(email, code.trim());
    setLoading(false);
    if (err || !data?.mfaToken) {
      setError(err?.message || t.resetErrorFallback);
      return;
    }
    navigate('/login/mfa-setup', { state: { mfaToken: data.mfaToken } });
  };

  return (
    <Container className="h-full">
      <div className="col-span-12 flex min-h-0 items-center justify-center py-4">
        <div className="flex max-h-[90dvh] w-full max-w-[430px] flex-col items-start overflow-auto rounded-2xl bg-white p-8 shadow-lg">
          <h2 className="mb-2 font-haffer text-sh-30-38 font-bold leading-9 tracking-tight text-black-200">
            {t.resetCodeTitle}
          </h2>
          <p className="mb-8 font-haffer text-sh-sm text-dark-grey">{t.resetCodeDescription}</p>

          <form className="flex w-full flex-col items-start" onSubmit={onSubmit}>
            <div className="mb-6 w-full">
              <Label>
                <p className="mb-2 font-haffer text-sh-xs text-dark-grey">{t.resetCodeLabel}</p>
              </Label>
              <Input
                ref={codeInputRef}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder={t.codePlaceholder}
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              {error && <FieldError message={error} />}
            </div>

            <Button type="submit" theme="solid" className="mb-4" showChevron={false} disabled={loading}>
              {loading ? t.loadingLabel : t.resetCodeCta}
            </Button>
          </form>

          <Link
            className="text-center font-haffer text-sh-xs font-bold text-people-teal-600 hover:!text-people-blue-600"
            to="/login">
            {t.backToSignInCta}
          </Link>
        </div>
      </div>
    </Container>
  );
}
